import { useState } from 'react'
import type { View } from './derive'
import { FoundOnNetwork } from './FoundOnNetwork'
import { GroupPanel } from './GroupPanel'
import { MachinePanel } from './MachinePanel'
import { MachineRows } from './MachineRows'
import { SelectionBar } from './SelectionBar'

type Status = 'all' | 'offline' | 'not_joined' | 'online'
const ORDER = { offline: 0, not_joined: 1, online: 2 } as const

/**
 * Every machine, and the place groups are built: filter to what belongs
 * together (a site, a search), tick, and make a group of it. Problems sort
 * first — at 200 robots the screen should open on what is wrong.
 */
export function MachinesTab({ view }: { view: View }): JSX.Element {
  const [q, setQ] = useState('')
  const [status, setStatus] = useState<Status>('all')
  const [groupId, setGroupId] = useState('')
  const [site, setSite] = useState('')
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
  const [open, setOpen] = useState<string | null>(null)
  const [making, setMaking] = useState(false)

  const sites = [...new Set(view.machines.map((m) => m.robot?.site).filter((s): s is string => !!s))].sort()
  const needle = q.trim().toLowerCase()
  const shown = view.machines
    .filter((m) => status === 'all' || m.net === status)
    .filter((m) => !groupId || m.groupIds.includes(groupId))
    .filter((m) => !site || m.robot?.site === site)
    .filter((m) => !needle || m.name.toLowerCase().includes(needle) || m.tag.includes(needle) || (m.device?.addresses[0] ?? '').includes(needle))
    .sort((a, b) => ORDER[a.net] - ORDER[b.net] || a.name.localeCompare(b.name, undefined, { numeric: true }))
  const count = (s: Status) => view.machines.filter((m) => m.net === s).length
  const machine = open ? view.machine(open) : undefined

  return (
    <div className="stack" style={{ gap: 14 }}>
      <FoundOnNetwork view={view} />
      <div className="cx-toolbar">
        <input className="cx-search" aria-label="Find a machine" placeholder="Find a robot, a computer, a tag or an address" value={q} onChange={(e) => setQ(e.currentTarget.value)} />
        <div className="segmented" role="tablist" aria-label="Status">
          {([['all', 'All'], ['offline', `Offline · ${count('offline')}`], ['not_joined', `Not joined · ${count('not_joined')}`], ['online', 'Online']] as const).map(([id, label]) => (
            <button key={id} role="tab" aria-selected={status === id} onClick={() => setStatus(id)}>{label}</button>
          ))}
        </div>
        <select value={groupId} aria-label="Group" onChange={(e) => setGroupId(e.currentTarget.value)}>
          <option value="">Every group</option>
          {view.groups.map((g) => (
            <option key={g.id} value={g.id}>{g.name} · {g.machineIds.length}</option>
          ))}
        </select>
        {sites.length ? (
          <select value={site} aria-label="Site" onChange={(e) => setSite(e.currentTarget.value)}>
            <option value="">Every site</option>
            {sites.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        ) : null}
      </div>

      {selected.size ? (
        <SelectionBar view={view} selected={selected} filteredGroup={groupId ? view.group(groupId) : undefined} onNewGroup={() => setMaking(true)} onDone={() => setSelected(new Set())} />
      ) : null}

      <div className="cx-columns">
        <div className="cx-main">
          <MachineRows view={view} machines={shown} selected={selected} onSelect={setSelected} open={open} onOpen={setOpen} total={view.machines.length} />
        </div>
        <aside className="cx-side">
          {machine ? (
            <MachinePanel view={view} machine={machine} onClose={() => setOpen(null)} />
          ) : (
            <section className="cx-panel cx-panel--padded">
              <h2 className="cx-panel__title">Press a machine</h2>
              <p className="muted">to see who can SSH into it and why, its address, and how to join it to the network.</p>
              <p className="muted">To make a group: filter to the machines that belong together, tick them (or “Select all shown”), and press “New group”.</p>
            </section>
          )}
        </aside>
      </div>
      {making ? <GroupPanel view={view} groupId={null} preset={[...selected]} onClose={() => (setMaking(false), setSelected(new Set()))} /> : null}
    </div>
  )
}
